// ────────────────────────────────────────────────────────────────────
// "One asset, several components — each with its own minimum"
// (design_handoff/README.md:202-205).
//
// WHY EVERY ROW CARRIES NUMBERS. README.md:342 is the rule the whole screen
// rests on — *the asset's interval is the worst component's, never an average*
// — and a table that printed a rate on the governing row and dashes on the
// rest cannot be argued with. The handoff's own gap #5 records that in the
// prototype these were authored by hand; task 2 derives them per component
// (`assemble._component_zones` → `ComponentIntegrity`) so the rule is
// checkable rather than assumed.
//
// NOTHING HERE IS DERIVED. The prototype computed each row's remaining life as
// `(last - t_min) / rate` and its due date from a cap, in the browser. Both are
// on the record now, from `rules.py`, which owns the month FLOOR, the halving
// and the origin precedence. A second copy of any of that in TypeScript drifts
// from the first with nothing failing — the class of defect that moved all
// four rate sentences server-side in task 4b.
//
// WHICH ROW GOVERNS is the record's own `governs` flag, never `components[0]`.
// The wire happens to sort worst-first, and reading the position instead would
// agree with it right up until the day it stopped — the same positional read
// that made this screen call the oldest inspection the current one.
//
// A COMPONENT WITH ONE READING GETS NO RATE, AND NEVER A ZERO. `no_rate_reason`
// says which kind of nothing it is, and the tag column prints it: a 0.000 mm/yr
// on a patch plate installed last year reads as "not corroding" where the truth
// is "never measured twice" (README.md:319).
// ────────────────────────────────────────────────────────────────────
import type { CSSProperties, ReactNode } from "react";
import { EMDASH, mm, monthYear, rateMmYr, years } from "./format";
import type { AssetIntegrity, ComponentIntegrity, NoRateReason } from "./types";

const HEAD: CSSProperties = {
  fontSize: 8.5, letterSpacing: ".13em", color: "var(--ink-4)", whiteSpace: "nowrap",
};
const NUM: CSSProperties = {
  flex: "none", textAlign: "right", fontSize: 11, color: "var(--ink-3)",
};

/** The tag the handoff's last column carries. `GOVERNS` on the one row the
 *  record flags; otherwise the reason this component has no rate, in the
 *  record's own vocabulary — "new · no rate" is the handoff's wording for the
 *  single-reading case (XZYNEXA.dc.html:2675). "" when there is nothing to
 *  say, which is the normal shape of a rated, non-governing component. */
function tagFor(c: ComponentIntegrity): string {
  if (c.governs) return "GOVERNS";
  switch (c.no_rate_reason as NoRateReason) {
    case "single_inspection": return "new · no rate";
    case "no_baseline":       return "no baseline · no rate";
    // NOT the same row as `no_baseline`, and it wore that tag until the server
    // could tell the two apart. This component may have been read for a decade;
    // what it has none of is a minimum on file, so there is nothing for its
    // readings to be measured against.
    case "no_minimum":        return "no t-min · no rate";
    case "below_minimum":     return "under minimum";
    default:                  return "";
  }
}

function Cell({ width, children, style }: {
  width: number; children: ReactNode; style?: CSSProperties;
}): JSX.Element {
  return (
    <div className="font-mono" style={{ ...NUM, width, ...style }}>{children}</div>
  );
}

function Row({ c }: { c: ComponentIntegrity }): JSX.Element {
  const tag = tagFor(c);
  // The governing row's own colour, and the inset edge the handoff gives it.
  // Accent-as-text is --accent-deep; the 3px edge is a fill, so it keeps
  // --accent-2 (styles/HARDCODED-COLOURS.md).
  const ink = c.governs ? "var(--accent-deep)" : "var(--ink-1)";

  return (
    <div data-testid="component-row"
         data-label={c.label}
         data-governs={c.governs ? "true" : "false"}
         data-no-rate={c.no_rate_reason}
         style={{
           display: "flex", gap: 12, alignItems: "center", padding: "9px 16px",
           borderBottom: "1px solid var(--line-3)",
           background: c.governs ? "var(--surface-warm-3)" : "transparent",
           boxShadow: c.governs ? "inset 3px 0 0 var(--accent-2)" : undefined,
         }}>
      <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: ink,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {/* A BLANK LABEL IS A REAL ZONE, not a rendering accident. Readings that
            could not be placed on any component fall into the asset's fallback
            zone, whose label is "" (assemble._zone_specs), and this cell was
            simply empty — a row of numbers belonging to nothing. Say what the
            row is instead. */}
        {c.label || (
          <span style={{ color: "var(--ink-3)", fontStyle: "italic" }}>
            Readings not assigned to a component
          </span>
        )}
        {/* The parent design component, when the reading could be placed on
            one. "" is the normal shape of imported data and is left blank
            rather than guessed at (record.py ComponentIntegrity). */}
        {c.component && c.component !== c.label && (
          <span className="font-mono" style={{ fontSize: 9.5, color: "var(--ink-4)", marginLeft: 7 }}>
            {c.component}
          </span>
        )}
      </div>

      {/* NO MINIMUM ON FILE IS AN EM DASH, never "0.00". A 0.00 mm minimum is a
          statement about the part — that any wall thickness at all passes —
          and the fallback zone reaches this table carrying exactly that number
          (`float(asset.t_min_design_mm or 0)`). The tag column says which kind
          of nothing it is. */}
      <Cell width={70}>{c.t_min_mm > 0 ? mm(c.t_min_mm) : EMDASH}</Cell>
      <Cell width={80} style={{ fontSize: 12, fontWeight: 600, color: ink }}>
        {mm(c.last_reading_mm)}
      </Cell>
      {/* No rate is an em dash, never 0.000 — the tag says which kind of
          nothing it is. */}
      <Cell width={82}>
        {c.rates.governing == null ? EMDASH : rateMmYr(c.rates.governing)}
      </Cell>
      <Cell width={66}>{years(c.rates.remaining_life_years, 1)}</Cell>
      <Cell width={78} style={{ fontSize: 11, fontWeight: 500, color: ink }}>
        {monthYear(c.next_due)}
      </Cell>

      <div style={{ width: 104, flex: "none", display: "flex", justifyContent: "flex-end" }}>
        {tag && (
          <span className="font-mono"
                style={{
                  fontSize: 8, fontWeight: 600, letterSpacing: ".1em",
                  padding: "2px 6px", borderRadius: 20, whiteSpace: "nowrap",
                  background: c.governs ? "rgba(var(--accent-rgb) / 0.12)" : "var(--line-2)",
                  color: c.governs ? "var(--accent-deep)" : "var(--ink-3)",
                }}>
            {tag}
          </span>
        )}
      </div>
    </div>
  );
}

/** The paragraph under the heading. It names the governing component from the
 *  record — `governing_component` uses the same vocabulary the rows' `label`
 *  does — and states the rule the table exists to make checkable. */
function note(record: AssetIntegrity): string {
  const n = record.components.length;
  if (n === 0) {
    return "No components on file for this asset — its readings are not split into parts, so "
         + "there is one minimum and one rate, and this table has nothing to add to the strip "
         + "above.";
  }
  if (n === 1) {
    // ONLY TRUE IF THAT COMPONENT HAS A RATE. On an asset whose single
    // component carries no minimum, or one reading, the asset's figure above
    // does not come from this row at all — and the sentence said it did.
    const only = record.components[0];
    return only.rates.governing != null
      ? "One component on file, so the asset's rate is that component's rate."
      : "One component on file, and it carries no rate of its own — so the figure in the "
      + "strip above does not come from this table. The tag on the row says why.";
  }
  const worst = record.governing_component || "the worst of them";
  return `Each component carries its own minimum and its own rate. The asset is governed by the `
       + `worst of them — ${worst} — which is why the figure in the strip above is not an `
       + `average. A job that read only the healthy components would produce a longer interval `
       + `and be wrong.`;
}

export function ComponentsTable({ record }: { record: AssetIntegrity }): JSX.Element {
  return (
    <section style={{
      marginTop: 16, border: "1px solid var(--hairline)", borderRadius: 10,
      overflow: "hidden", background: "var(--paper-2)",
    }}>
      <div style={{ padding: "12px 16px 10px", borderBottom: "1px solid var(--hairline)" }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-1)", marginBottom: 3 }}>
          One asset, several components — each with its own minimum
        </div>
        <div data-testid="components-note"
             style={{ fontSize: 11.5, lineHeight: 1.55, color: "var(--ink-3)" }}>
          {note(record)}
        </div>
      </div>

      {record.components.length > 0 && (
        <>
          <div className="font-mono"
               style={{
                 display: "flex", gap: 12, padding: "9px 16px",
                 background: "var(--surface-2)", borderBottom: "1px solid var(--hairline)",
                 fontWeight: 500,
               }}>
            <span style={{ ...HEAD, flex: 1, minWidth: 0 }}>COMPONENT</span>
            <span style={{ ...HEAD, width: 70, flex: "none", textAlign: "right" }}>t-min</span>
            <span style={{ ...HEAD, width: 80, flex: "none", textAlign: "right" }}>LAST READ</span>
            <span style={{ ...HEAD, width: 82, flex: "none", textAlign: "right" }}>RATE</span>
            <span style={{ ...HEAD, width: 66, flex: "none", textAlign: "right" }}>REM. LIFE</span>
            <span style={{ ...HEAD, width: 78, flex: "none", textAlign: "right" }}>NEXT DUE</span>
            <span style={{ ...HEAD, width: 104, flex: "none" }} />
          </div>

          {/* Label PLUS ORDINAL. `_governing()`'s own docstring anticipates two
              components sharing a name ("which two components sharing a name
              would get wrong"), and two rows keyed alike is a React key
              collision — the second row's state and DOM node follow the first.
              The record's order is the server's order of badness, so the index
              is stable for a given record. */}
          {record.components.map((c, i) => <Row key={`${c.label}#${i}`} c={c} />)}
        </>
      )}
    </section>
  );
}
